import React, { useState, useEffect, useMemo, useRef } from "react";
import { Form, Select, Input, Row, Col, Typography, message } from "antd";
import { SearchOutlined, LoadingOutlined } from "@ant-design/icons";
import axios from "axios";
import debounce from "lodash/debounce";
import { FETCH_ALL_ARTICLE_API } from "../../../../Constants/Apis";

const { Text } = Typography;

// ---------- helpers ----------
// Accepts: "07", "12 15", "12-15", "1215", and "4244"
const parseSizes = (raw) => {
  if (raw == null) return [];
  const s = String(raw).trim();

  // "12 15" or "12-15" or "12..15"
  const m = s.match(/^(\d{1,2})\s*(?:-|\.{2}|\s)\s*(\d{1,2})$/);
  if (m) {
    const a = Number(m[1]);
    const b = Number(m[2]);
    if (!Number.isInteger(a) || !Number.isInteger(b) || b < a) return [];
    return Array.from({ length: b - a + 1 }, (_, i) =>
      String(a + i).padStart(2, "0")
    );
  }

  // "1215" or "4244" (compact range)
  if (/^\d{4}$/.test(s)) {
    const a = Number(s.slice(0, 2));
    const b = Number(s.slice(2, 4));
    if (!Number.isInteger(a) || !Number.isInteger(b) || b < a) return [];
    return Array.from({ length: b - a + 1 }, (_, i) =>
      String(a + i).padStart(2, "0")
    );
  }

  // single "7" or "07"
  if (/^\d{1,2}$/.test(s)) return [String(Number(s)).padStart(2, "0")];

  return [];
};

const toNumber = (x) =>
  Number(String(x ?? "").replace(/(?<=\d)[,.\s](?=\d{3}\b)/g, ""));

// -----------------------------

const StockCreationForm = ({
  modalForm,
  ColorData,
  isEditMode,
  isTempEditMode,
  stockData,
  onArticleSelect,
  userData,
}) => {
  const [sizeInput, setSizeInput] = useState("");
  const [isRange, setIsRange] = useState(false);
  const [articleOptions, setArticleOptions] = useState([]);
  const [articleLoading, setArticleLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedArticle, setSelectedArticle] = useState(null);

  const cancelSourceRef = useRef(null);

  // preload defaults in edit mode
  useEffect(() => {
    if (isEditMode && stockData) {
      // Reset form first to ensure clean state
      modalForm.resetFields();
      
      // Set form values with the stock data
      modalForm.setFieldsValue({
        article: stockData.article,
        color: stockData.color,
        size: stockData.size,
        assort: stockData.assort,
        price: stockData.price,
        harga_internal: stockData.harga_internal,
        quantity: stockData.quantity,
        currentQuantity: stockData.currentQuantity,
        receivedQuantity: stockData.receivedQuantity, // prefill received
        barcode: stockData.barcode,
        _id: stockData._id,
      });
      setSizeInput(stockData.size);
      setIsRange(parseSizes(stockData.size).length > 1);
    }
  }, [isEditMode, stockData, modalForm, isTempEditMode]);

  // remote search
  const fetchArticles = async (searchKeyword = "") => {
    try {
      if (cancelSourceRef.current) cancelSourceRef.current.cancel();
      cancelSourceRef.current = axios.CancelToken.source();

      setArticleLoading(true);
      const res = await axios.get(`${FETCH_ALL_ARTICLE_API}/dropdown`, {
        params: { searchKeyword, counterCode: userData?.counterCode },
        cancelToken: cancelSourceRef.current.token,
      });
      const articles = res.data?.data || [];
      setArticleOptions(articles);
      return articles;
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error("Error fetching articles:", err);
        message.error("Failed to fetch articles");
      }
      return [];
    } finally {
      setArticleLoading(false);
    }
  };

  const debouncedFetchArticles = useMemo(
    () =>
      debounce((v) => {
        fetchArticles(v);
      }, 450),
    []
  );

  useEffect(() => {
    fetchArticles();
    return () => {
      debouncedFetchArticles.cancel();
      if (cancelSourceRef.current) cancelSourceRef.current.cancel();
    };
  }, []);

  const handleArticlePicked = (value, option) => {
    const article = articleOptions.find((a) => a._id === option?.value);
    setSelectedArticle(article || null);

    // prefill prices if present
    if (article) {
      // Set default size range if minSize and maxSize are available
      let defaultSize = "";
      if (article.minSize && article.maxSize) {
        const minSize = String(article.minSize).padStart(2, "0");
        const maxSize = String(article.maxSize).padStart(2, "0");
        defaultSize = `${minSize}${maxSize}`;
      }

      modalForm.setFieldsValue({
        article: article.name,
        brand: article.brand, // Include brand for SKU generation
        price: article.hpp ?? "",
        harga_internal: article.harga_internal ?? "",
        size: defaultSize,
        color: "", // Reset color selection
        assort: "", // Reset assort
      });
      
      // Update size input state
      setSizeInput(defaultSize);
      setIsRange(defaultSize && parseSizes(defaultSize).length > 1);
      
      if (onArticleSelect) onArticleSelect(article);
    }
  };

  const handleArticleSearch = (v) => {
    setSearchText(v);
    debouncedFetchArticles(v);
  };

  // build barcode when color/size/article changes
  const recomputeBarcode = () => {
    const colorName = modalForm.getFieldValue("color");
    const size = modalForm.getFieldValue("size");
    if (!selectedArticle || !colorName || !size) return;

    const color = (ColorData || []).find((c) => c.name === colorName);
    const colorCode = color?.code ? String(color.code).padStart(3, "0") : "";
    const sizes = parseSizes(size);

    const s2 = sizes[0] || "";
    const base = `${selectedArticle.preBarcode || ""}${colorCode || ""}`;
    if (!base) return;

    modalForm.setFieldsValue({
      barcode: `${base}${s2}`,
    });
  };

  // live form change handler
  const handleFormChange = (changedFields, allValues) => {
    const { size, assort, quantity, receivedQuantity } = allValues;

    // keep sizeInput for controlled display & range flag
    if (typeof size === "string") {
      setSizeInput(size);
      setIsRange(parseSizes(size).length > 1);
    }

    // live recompute barcode base+color+first size
    recomputeBarcode();

    // if receivedQuantity changed, auto-sync currentQuantity to it (editable later)
    if (
      Object.prototype.hasOwnProperty.call(changedFields, "receivedQuantity")
    ) {
      const rq = toNumber(receivedQuantity);
      const current = toNumber(modalForm.getFieldValue("currentQuantity"));
      if (Number.isFinite(rq) && rq !== current) {
        modalForm.setFieldsValue({ currentQuantity: rq });
      }
    }

    // if range, validate assort length live (only when not in temp edit)
    if (!isTempEditMode && isRange && assort && quantity) {
      const sizes = parseSizes(size);
      const count = sizes.length;

      const aList = Array.isArray(assort)
        ? assort
        : /^\d+$/.test(String(assort)) && String(assort).length === sizes.length
        ? String(assort).split("")
        : String(assort)
            .split(/[,\s]+/)
            .filter(Boolean);

      if (aList.length && aList.length !== count) {
        modalForm.setFieldsValue({ assort: "" });
        message.error(`Assort length must match size range count (${count}).`);
      } else if (aList.some((x) => !/^\d+$/.test(String(x)))) {
        modalForm.setFieldsValue({ assort: "" });
        message.error("Assort must contain non-negative integers.");
      } else if (toNumber(quantity) <= 0) {
        message.error("Quantity per pack must be a positive integer.");
      }
    }
  };

  // options for articles in v5 Select (server-filtered)
  const articleSelectOptions = useMemo(
    () =>
      articleOptions.map((a) => ({
        label: `${a.name} - ${a.code}`,
        value: a._id,
      })),
    [articleOptions]
  );

  // options for colors - filter by selected article's colorList
  const colorOptions = useMemo(() => {
    if (!selectedArticle || !selectedArticle.colorList || selectedArticle.colorList.length === 0) {
      return (ColorData || []).map((c) => ({ label: c.name, value: c.name }));
    }
    
    // Filter colors based on selected article's colorList
    const availableColors = (ColorData || []).filter((color) =>
      selectedArticle.colorList.includes(color._id)
    );
    
    return availableColors.map((c) => ({ label: c.name, value: c.name }));
  }, [ColorData, selectedArticle]);

  return (
    <Form form={modalForm} layout="vertical" onValuesChange={handleFormChange}>
      <Form.Item
        label="Article"
        name="article"
        rules={[{ required: true, message: "Please select Article!" }]}
      >
        <Select
          placeholder="Search for an article"
          showSearch
          disabled={isEditMode}
          loading={articleLoading}
          // we store 'article' field as name, but Select uses _id; so we map onSelect
          onSelect={(v, option) => handleArticlePicked(v, option)}
          onSearch={handleArticleSearch}
          filterOption={false}
          allowClear
          suffixIcon={articleLoading ? <LoadingOutlined /> : <SearchOutlined />}
          dropdownRender={(menu) => (
            <div>
              <div
                style={{
                  padding: "8px 12px",
                  color: "#8c8c8c",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                {articleLoading
                  ? "Searching..."
                  : articleOptions.length > 0
                  ? `Found ${articleOptions.length} article(s)`
                  : searchText
                  ? `No articles found matching "${searchText}"`
                  : "Type to search articles"}
              </div>
              {menu}
            </div>
          )}
          options={articleSelectOptions}
          labelInValue={false}
        />
      </Form.Item>

      <Form.Item
        label="Warna"
        name="color"
        rules={[{ required: true, message: "Please select Warna!" }]}
      >
        <Select
          placeholder="Select a color"
          showSearch
          disabled={isEditMode || !selectedArticle}
          options={colorOptions}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          onChange={recomputeBarcode}
        />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Size"
            name="size"
            rules={[
              { required: true, message: "Please input Size!" },
              () => ({
                validator(_, v) {
                  const sizes = parseSizes(v);
                  if (!v || sizes.length === 0) {
                    return Promise.reject(
                      "Use '07', '12 15', '12-15', '1215', or '4244'."
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input
              value={sizeInput}
              onChange={(e) => {
                const val = e.target.value;
                setSizeInput(val);
                modalForm.setFieldsValue({ size: val }); // keep form in sync
                setIsRange(parseSizes(val).length > 1);
                if (!isEditMode) modalForm.setFieldsValue({ assort: "" });
              }}
              maxLength={5}
              disabled={isEditMode || !selectedArticle}
              placeholder="e.g. '07' or '12 15' or '4244'"
              onWheel={(e) => e.currentTarget.blur()}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Assort"
            name="assort"
            rules={[
              { required: true, message: "Please input Assort!" },
              () => ({
                validator(_, v) {
                  const sizes = parseSizes(modalForm.getFieldValue("size"));
                  const isRange = sizes.length > 1;

                  let list = [];
                  const str = String(v ?? "").trim();

                  if (Array.isArray(v)) {
                    list = v;
                  } else if (
                    /^\d+$/.test(str) &&
                    isRange &&
                    str.length === sizes.length
                  ) {
                    list = str.split("");
                  } else {
                    list = str.split(/[,\s]+/).filter(Boolean);
                  }

                  if (!list.length) return Promise.reject("Assort required.");
                  if (list.some((x) => !/^\d+$/.test(String(x)))) {
                    return Promise.reject(
                      "Assort must be non-negative integers."
                    );
                  }
                  if (isRange && list.length !== sizes.length) {
                    return Promise.reject(
                      `Assort length must match range count (${sizes.length}).`
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input
              disabled={isEditMode || !selectedArticle}
              placeholder={isRange ? "e.g. 1 1 0 2" : "e.g. 1"}
              onWheel={(e) => e.currentTarget.blur()}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Harga"
            name="price"
            rules={[
              { required: true, message: "Please input Harga!" },
              () => ({
                validator(_, v) {
                  const n = toNumber(v);
                  if (Number.isFinite(n) && n >= 0) return Promise.resolve();
                  return Promise.reject("Harga must be ≥ 0.");
                },
              }),
            ]}
          >
            <Input
              prefix="Rp. "
              type="number"
              disabled={isEditMode || !selectedArticle}
              onWheel={(e) => e.currentTarget.blur()}
              min={0}
              step="any"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Quantity"
            name="quantity"
            rules={[
              { required: true, message: "Please input Quantity!" },
              () => ({
                validator(_, v) {
                  const n = toNumber(v);
                  if (Number.isInteger(n) && n > 0) return Promise.resolve();
                  return Promise.reject("Must be a positive integer.");
                },
              }),
            ]}
          >
            <Input
              type="number"
              disabled={isEditMode || !selectedArticle}
              onWheel={(e) => e.currentTarget.blur()}
              min={1}
              step={1}
            />
          </Form.Item>
        </Col>
      </Row>

      {isEditMode && (
        <>
          <Row gutter={16}>
            {/* RECEIVED on the LEFT */}
            <Col span={12}>
              <Form.Item
                label="Received Quantity"
                name="receivedQuantity"
                rules={[
                  {
                    required: true,
                    message: "Please input Received Quantity!",
                  },
                  () => ({
                    validator(_, v) {
                      const rq = toNumber(v);
                      if (!Number.isInteger(rq) || rq < 0) {
                        return Promise.reject("Must be an integer ≥ 0.");
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
                extra={
                  <Text type="secondary">
                    Changing this will auto-set Current Quantity (you can still
                    edit it).
                  </Text>
                }
              >
                <Input 
                  type="number" 
                  disabled={!selectedArticle}
                  onWheel={(e) => e.currentTarget.blur()} 
                />
              </Form.Item>
            </Col>

            {/* CURRENT on the RIGHT, must be ≤ received */}
            <Col span={12}>
              <Form.Item
                label="Current Quantity"
                name="currentQuantity"
                rules={[
                  { required: true, message: "Please input Current Quantity!" },
                  () => ({
                    validator(_, v) {
                      const cq = toNumber(v);
                      const rq = toNumber(
                        modalForm.getFieldValue("receivedQuantity")
                      );
                      if (!Number.isInteger(cq) || cq < 0) {
                        return Promise.reject("Must be an integer ≥ 0.");
                      }
                      if (Number.isInteger(rq) && cq > rq) {
                        return Promise.reject(
                          "Current Quantity cannot exceed Received Quantity."
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
                extra={
                  <Text type="secondary">Must be ≤ Received Quantity.</Text>
                }
              >
                <Input 
                  type="number" 
                  disabled={!selectedArticle}
                  onWheel={(e) => e.currentTarget.blur()} 
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      {/* Hidden fields */}
      <Form.Item name="barcode" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="brand" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="harga_internal" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="_id" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
    </Form>
  );
};

export default StockCreationForm;
